import { useEffect, useRef, useState } from 'react';
import { Button, Input, Label } from '@ai-tutor/ui';
import { AlertTriangle, Camera, Loader2 } from 'lucide-react';
import { PersonalSectionSkeleton } from '../SettingsSkeleton';
import { ErrorState } from '@/components/core/common/boundaries/ErrorState';
import { notify } from '@/lib/notify';
import { newPasswordSchema, PASSWORD_HINT } from '@/lib/schemas/auth';
import { useAuth } from '@/contexts/AuthContext';
import {
  useUserProfile,
  useUpdateProfile,
  useUploadAvatar,
  useDeleteAccount,
} from '@/hooks/account/useUser';

const MAX_AVATAR_BYTES = 2 * 1024 * 1024; // 2MB

const getInitials = (name: string | null | undefined, email: string) => {
  const source = (name && name.trim()) || email;
  return source
    .split(/[\s@.]+/)
    .filter(Boolean)
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

export function PersonalSection() {
  const { data: profile, isLoading, isError, refetch } = useUserProfile();
  const updateProfile = useUpdateProfile();
  const uploadAvatar = useUploadAvatar();
  const { updatePassword, signOut, user } = useAuth();
  const deleteAccount = useDeleteAccount();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [fullName, setFullName] = useState('');
  const [bio, setBio] = useState('');

  // Password change
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [updatingPassword, setUpdatingPassword] = useState(false);

  // Delete account
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  useEffect(() => {
    if (profile) {
      setFullName(profile.full_name ?? '');
      setBio(profile.bio ?? '');
    }
  }, [profile]);

  if (isLoading) {
    return <PersonalSectionSkeleton />;
  }

  // A FAILED fetch used to fall through to the skeleton too — `!profile` is
  // true on error as well as while loading, so the Account panel shimmered
  // forever with no error, no retry, and nothing to distinguish it from a slow
  // network. Every other surface in the app that can fail says so and offers a
  // way back (`GraphStates`, the progress banner); this one silently did not.
  if (isError || !profile) {
    return (
      <ErrorState
        title="Couldn't load your account"
        description='Your profile did not come back. Nothing has changed — try again.'
        primaryAction={{ label: 'Try again', onClick: () => void refetch() }}
      />
    );
  }

  const initials = getInitials(profile.full_name, profile.email);

  const isDirty =
    fullName !== (profile.full_name ?? '') || bio !== (profile.bio ?? '');

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    const file = input.files?.[0];
    if (!file) return;

    // Cleared whatever happens next. A file input only fires `change` when
    // its selection CHANGES, and this used to be reset on success alone — so
    // after a refused or failed upload, choosing the same photo again (the
    // obvious retry) did nothing at all.
    try {
      if (file.size > MAX_AVATAR_BYTES) {
        notify.error('Image must be 2MB or smaller');
        return;
      }
      await notify.promise(uploadAvatar.mutateAsync(file), {
        loading: 'Uploading avatar…',
        success: 'Avatar updated',
        error: 'Failed to upload avatar',
      });
    } catch {
      // The toast has already said so.
    } finally {
      input.value = '';
    }
  };

  const handleSave = async () => {
    if (!isDirty) return;

    try {
      await notify.promise(
        updateProfile.mutateAsync({
          full_name: fullName.trim() || undefined,
          bio: bio.trim() || undefined,
        }),
        {
          loading: 'Saving…',
          success: 'Changes saved',
          error: 'Failed to save changes',
        }
      );
    } catch {
      // The toast has already said so, and the edit stays in the form for a
      // retry. Rethrowing only escaped the click as an unhandled rejection.
    }
  };

  // A Google-only account has no password yet. The same form adds one -
  // Supabase attaches it to this account - so it is relabelled rather than
  // hidden: "Change" would imply a password they never chose.
  const providers = (user?.app_metadata?.providers as string[] | undefined) ?? [
    'email',
  ];
  const hasPassword = providers.includes('email');

  const handleUpdatePassword = async () => {
    // The SAME `updatePassword` the recovery screen calls, so it has to accept
    // the same passwords. This used to check a local `MIN_PASSWORD_LENGTH = 8`
    // and nothing else, which let Settings set a password the reset screen
    // would have rejected. `newPasswordSchema` is the one policy.
    const parsed = newPasswordSchema.safeParse({ password: newPassword });
    if (!parsed.success) {
      notify.error(parsed.error.issues[0].message);
      return;
    }
    if (newPassword !== confirmPassword) {
      notify.error('Passwords do not match');
      return;
    }

    setUpdatingPassword(true);
    try {
      const { error } = await updatePassword(newPassword);
      if (error) {
        notify.error(error.message || 'Failed to update password');
        return;
      }
      notify.success(hasPassword ? 'Password updated' : 'Password set');
      setNewPassword('');
      setConfirmPassword('');
    } finally {
      setUpdatingPassword(false);
    }
  };

  const expectedDeleteText = profile?.email ?? user?.email ?? '';
  const canDelete =
    deleteConfirmText.trim().toLowerCase() ===
    expectedDeleteText.trim().toLowerCase();

  const handleDeleteAccount = async () => {
    if (!canDelete) return;

    try {
      await notify.promise(deleteAccount.mutateAsync(), {
        loading: 'Deactivating account…',
        success: 'Account deactivated. You have 30 days to restore it.',
        error: 'Failed to deactivate account',
      });
      // Sign out so the now-banned session is cleared and the user is
      // redirected to the auth page (where they can request a restore link).
      await signOut();
    } catch {
      // toast already surfaced the error
    }
  };

  return (
    <div className='space-y-8'>
      {/* Profile card - avatar + name/email/bio share the same bordered
          container as Change Password below, so the section reads as a
          stack of equally-weighted cards instead of free-floating fields
          followed by cards. */}
      <div className='rounded-lg border border-border p-5'>
        <h3 className='mb-1 text-sm font-semibold text-foreground'>Profile</h3>
        <p className='mb-5 text-xs text-muted-foreground'>
          How you appear across the app.
        </p>

        <div className='space-y-6'>
          {/* Avatar */}
          <div className='flex items-center gap-5'>
            <div className='relative'>
              {profile.avatar_url ? (
                <img
                  src={`${profile.avatar_url}?v=${encodeURIComponent(profile.updated_at)}`}
                  alt={profile.full_name ?? profile.email}
                  className='h-16 w-16 rounded-full object-cover'
                />
              ) : (
                <div className='flex h-16 w-16 items-center justify-center rounded-full bg-primary text-xl font-semibold text-primary-foreground'>
                  {initials}
                </div>
              )}
              {/* A 44px button with the 28px badge drawn inside it: the badge
                  sits on the avatar's corner, too small for a thumb on its
                  own. Absolutely positioned, so the hit area is the button
                  itself rather than a `tap-target` overlay. */}
              <button
                onClick={handleAvatarClick}
                disabled={uploadAvatar.isPending}
                className='group/photo absolute -bottom-3 -right-3 flex h-11 w-11 items-center justify-center rounded-full disabled:opacity-60'
                aria-label='Change profile photo'
              >
                <span className='flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-muted text-muted-foreground transition-colors group-hover/photo:bg-secondary dark:border-gray-800'>
                  {uploadAvatar.isPending ? (
                    <Loader2 className='h-3.5 w-3.5 animate-spin' />
                  ) : (
                    <Camera className='h-3.5 w-3.5' />
                  )}
                </span>
              </button>
              <input
                ref={fileInputRef}
                type='file'
                accept='image/png,image/jpeg,image/jpg,image/gif'
                onChange={handleAvatarChange}
                className='hidden'
              />
            </div>
            <div>
              <p className='text-sm font-medium text-foreground'>
                Profile Photo
              </p>
              <p className='text-xs text-muted-foreground'>
                JPG, PNG or GIF. Max 2MB.
              </p>
            </div>
          </div>

          {/* Form Fields */}
          <div className='grid grid-cols-1 gap-5 sm:grid-cols-2'>
            <div className='space-y-1.5'>
              <Label htmlFor='name'>Full Name</Label>
              <Input
                id='name'
                value={fullName}
                onChange={e => setFullName(e.target.value)}
                placeholder='Your name'
                className='bg-card'
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='email'>Email Address</Label>
              <Input
                id='email'
                type='email'
                value={profile.email}
                disabled
                className='bg-card'
              />
              <p className='text-xs text-muted-foreground'>
                Managed by your authentication provider.
              </p>
            </div>
          </div>

          <div className='space-y-1.5'>
            <Label htmlFor='bio'>Bio</Label>
            <textarea
              id='bio'
              rows={3}
              value={bio}
              onChange={e => setBio(e.target.value)}
              placeholder='Tell us a bit about yourself.'
              className='flex w-full rounded-md border border-input bg-card px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50'
            />
          </div>

          <div className='mt-5'>
            <Button
              size='sm'
              className='max-sm:h-11'
              onClick={handleSave}
              disabled={!isDirty || updateProfile.isPending}
            >
              {updateProfile.isPending ? (
                <>
                  <Loader2 className='mr-2 h-3.5 w-3.5 animate-spin' />
                  Saving…
                </>
              ) : (
                'Save'
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Change Password */}
      <div className='rounded-lg border border-border p-5'>
        <h3 className='mb-1 text-sm font-semibold text-foreground'>
          {hasPassword ? 'Change Password' : 'Set a Password'}
        </h3>
        <p className='mb-4 text-xs text-muted-foreground'>
          {hasPassword
            ? "You'll stay signed in after updating."
            : 'You sign in with Google. Add a password to also sign in with your email.'}
        </p>
        <div className='max-w-sm space-y-4'>
          <div className='space-y-1.5'>
            <Label htmlFor='new-password'>New Password</Label>
            <Input
              id='new-password'
              type='password'
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              autoComplete='new-password'
              className='bg-card'
            />
            {/* The rule, stated before submission rather than discovered as a
                toast afterwards. Same constant the recovery screen renders, so
                the two cannot describe different policies. */}
            <p className='text-xs text-muted-foreground'>{PASSWORD_HINT}</p>
          </div>
          <div className='space-y-1.5'>
            <Label htmlFor='confirm-password'>Confirm New Password</Label>
            <Input
              id='confirm-password'
              type='password'
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              placeholder='Re-enter new password'
              autoComplete='new-password'
              className='bg-card'
            />
          </div>
          <Button
            size='sm'
            className='max-sm:h-11'
            onClick={handleUpdatePassword}
            disabled={updatingPassword || !newPassword || !confirmPassword}
          >
            {updatingPassword ? (
              <>
                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                Updating…
              </>
            ) : hasPassword ? (
              'Update Password'
            ) : (
              'Set Password'
            )}
          </Button>
        </div>
      </div>

      {/* Delete Account - colour palette mirrors the destructive toast
          (#FEF2F2 bg / #FECACA border / #991B1B text) so the banner reads as
          a soft warning surface rather than a saturated alert. */}
      <div className='rounded-lg border border-red-200 bg-red-50 p-5 dark:border-red-900/40 dark:bg-red-950/30'>
        <div className='flex items-start gap-3'>
          <AlertTriangle className='mt-0.5 h-5 w-5 flex-shrink-0 text-red-800 dark:text-red-300' />
          <div className='flex-1'>
            <h3 className='mb-1 text-sm font-semibold text-red-800 dark:text-red-300'>
              Delete Account
            </h3>
            <p className='mb-2 text-sm text-red-800/90 dark:text-red-300/90'>
              Your account will be deactivated immediately and your data will be
              permanently deleted after 30 days.
            </p>
            <p className='mb-4 text-xs text-red-800/75 dark:text-red-300/75'>
              Within 30 days you can restore your account from the sign-in
              screen by requesting a recovery link. After that, your lessons,
              mindspace, sources, and progress are permanently erased and cannot
              be recovered.
            </p>

            {!showDeleteConfirm ? (
              <Button
                variant='destructive'
                size='sm'
                onClick={() => setShowDeleteConfirm(true)}
                className='tap-target max-sm:h-11'
              >
                Delete Account
              </Button>
            ) : (
              <div className='space-y-3'>
                <div className='space-y-1.5'>
                  <Label
                    htmlFor='delete-confirm'
                    className='text-red-800 dark:text-red-300'
                  >
                    Type{' '}
                    <span className='font-mono font-semibold'>
                      {expectedDeleteText}
                    </span>{' '}
                    to confirm
                  </Label>
                  <Input
                    id='delete-confirm'
                    value={deleteConfirmText}
                    onChange={e => setDeleteConfirmText(e.target.value)}
                    placeholder={expectedDeleteText}
                    className='max-w-sm border-red-200 transition-colors hover:border-red-400 focus:border-red-400 dark:border-red-900/40 dark:hover:border-red-700 dark:focus:border-red-700'
                    autoComplete='off'
                  />
                </div>
                <div className='flex gap-2'>
                  <Button
                    variant='destructive'
                    size='sm'
                    className='max-sm:h-11'
                    onClick={handleDeleteAccount}
                    disabled={!canDelete || deleteAccount.isPending}
                  >
                    {deleteAccount.isPending ? (
                      <>
                        <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                        Deactivating…
                      </>
                    ) : (
                      'Deactivate Account'
                    )}
                  </Button>
                  <Button
                    variant='outline'
                    size='sm'
                    className='max-sm:h-11'
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setDeleteConfirmText('');
                    }}
                    disabled={deleteAccount.isPending}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
